import { useLayoutEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { type ScrollAlignT, SCROLL_ALIGN } from '../../../common/virtual-grid';
import { spanResizePreviewAtom } from '../atoms/frame-timeline-v4';
import { getAutoScrollTarget } from '../utils/frame-timeline-v4';

/**
 * Follows the edge being dragged during a span resize: reuses the playhead's lead-in
 * buffer so the edge never sits flush against the viewport border, and `auto` alignment
 * means it only scrolls once the edge would move beyond what's currently visible.
 *
 * Layout effect (not passive) so the scroll and the resized span edge commit in the same
 * paint — otherwise the edge jumps past the pointer and snaps back once the scroll lands.
 */
export function useSpanResizeAutoScroll(scrollToColumn: (index: number, align?: ScrollAlignT) => void, totalFrames: number) {
  const frame = useAtomValue(spanResizePreviewAtom)?.frame ?? null;
  const prevFrameRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (frame === null) {
      prevFrameRef.current = null;
      return;
    }
    const prevFrame = prevFrameRef.current;
    const direction = prevFrame === null || frame >= prevFrame ? 1 : -1;
    prevFrameRef.current = frame;
    scrollToColumn(getAutoScrollTarget(frame, direction, totalFrames), SCROLL_ALIGN.auto);
  }, [frame, totalFrames, scrollToColumn]);
}